import { useTranslation } from 'react-i18next';
import { Box, Chip, Skeleton, Stack, Typography, useTheme } from '@mui/material';

import { SALE_ORDER_STATUS } from '@bandido/shared/constants';

import { SearchInput } from '@frontend/features/common/components/SearchInput/SearchInput';
import { usePagination, Table } from '@frontend/features/common/components/Table';
import { ListLayout } from '@frontend/features/common/components/ListLayout';
import { useGetSaleOrdersForDashboard } from '@frontend/features/sale-orders/queries';
import { Link } from '@frontend/features/common/components/Link';
import { formatISOStringToFriendlyDate } from '@frontend/features/common/helper';
import { useDebouncedState } from '@frontend/features/common/hooks/useDebouncedState';

import { routes } from '../routes';
import { defaultLeadingZeros } from '@frontend/features/common/constants';
import { addLeadingZeros } from '@frontend/features/common/utils/helpers';

export const saleOrderChipColor: Record<SALE_ORDER_STATUS, 'warning' | 'success' | 'error'> = {
  [SALE_ORDER_STATUS.Lead]: 'warning',
  [SALE_ORDER_STATUS.Active]: 'warning', 
  [SALE_ORDER_STATUS.Sold]: 'success',
  [SALE_ORDER_STATUS.Canceled]: 'error',
} as const;

export const SaleOrderList = () => {
  const { t } = useTranslation();
  const [search, setSearch] = useDebouncedState('', 300);
  const theme = useTheme();
  const pagination = usePagination();

  
  const { data, isLoading } = useGetSaleOrdersForDashboard({
    pageSize: pagination.rowsPerPage,
    currentPage: pagination.page,
    search: search,
  });

  const handleOnSearch = (value: string) => {
    setSearch(value);
  };

  return (
    <ListLayout title={t('saleOrder.list.title')} subtitle={t('saleOrder.list.subtitle')}>
      <Box display="flex" alignItems="center" gap={theme.spacing(3)} marginBottom={theme.spacing(3)}>
        <Typography
          variant="subtitle1"
          align="left"
          color={theme.palette.text.primary}
          sx={{
            flexGrow: 1,
          }}
        >
          {data?.items.length || 0} {t('saleOrder.list.results')}
        </Typography>

        <SearchInput placeholder={t('common.search')} onSearch={handleOnSearch} />
      </Box>

      {data && (
        <Table
          columns={[
            {
              id: 'id',
              label: t('saleOrder.list.table.id'),
              align: 'left',
              format: (_, saleOrder) => (
                <Stack>
                  <Link to={routes.dashboard.saleOrder.detail.replace(':id', String(saleOrder.id))}>{saleOrder.record}</Link>
                </Stack>
              )
            },
            {
              id: 'amount',
              label: t('saleOrder.list.table.amount.title'),
              align: 'left',
              format: (value) => value ? <>{t('saleOrder.list.table.amount.value', { value })}</> : <>{t('common.tbd')}</>
            },
            {
              id: 'status',
              label: t('saleOrder.list.table.status'),
              align: 'left',
              format: (_, saleOrder) => (<Chip label={t(`saleOrder.status.${saleOrder.status}`)} color={saleOrderChipColor[saleOrder.status as SALE_ORDER_STATUS] || 'default'} />)
            },
            {
              id: 'createdByName',
              label: t('saleOrder.list.table.createdBy'),
              align: 'left',
              format: (_, saleOrder) => (
                <Stack>
                  <Link to={routes.dashboard.userView.replace(':id', String(saleOrder.authorUserId))}>{saleOrder.authorName} {saleOrder.authorSurname}
                  </Link>
                </Stack>
              )
            },
            {
              id: 'convertedAt',
              label: t('saleOrder.list.table.convertedAt'),
              align: 'left',
              format: (_, so) => <>{formatISOStringToFriendlyDate(so.convertedAt || so.createdAt)}</>
            },
            {
              id: 'caseId',
              label: t('saleOrder.list.table.case'),
              align: 'left',
              minWidth: 90,
              format: (_, saleOrder) => (
                saleOrder.caseId ? (
                  <Link
                    target="_blank"
                    to={routes.dashboard.caseView.replace(':id', String(saleOrder.caseId))}
                    underline="none"
                  >
                    <Typography variant="body2" color="text.primary">
                      {addLeadingZeros(saleOrder.caseId, defaultLeadingZeros)}
                    </Typography>
                  </Link>
                ) : (
                  <Typography variant="body2" color="text.primary">
                    {t('common.unassigned')}
                  </Typography>
                )
              )
            },
          ]}
          data={{
            key: 'id',
            items: data.items,
            totalCount: data.count
          }}
          pagination={pagination}
          stickyHeader
        />
      )}
      {isLoading && <Skeleton height={theme.spacing(10)} variant="rounded" animation="wave" />}
    </ListLayout>
  );
};
